<script setup>
import axios from "axios";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

const { plans, plansBenefits, documentId, templateId } = defineProps(["plans", "plansBenefits", "showCloseButton", "documentId", "templateId"]);
const emit = defineEmits(["closeModal"]);

const { t, locale } = useI18n();

const createSubscription = async (plan, provider) => {
  // For subscription, redirect back to current document-questions page if we have documentId and templateId
  const successUrl = (documentId && templateId)
    ? `${window.location.origin}/document-questions?template=${templateId}&documentId=${documentId}`
    : window.location.href; // Fallback to current page
  // Use current URL as cancel URL with a parameter to indicate cancelled payment
  const cancelUrl = `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}payment_cancelled=true`;

  await axios
    .post("/api/user/subscription/create", {
      plan: plan,
      provider: provider,
      success_url: successUrl,
      cancelUrl: cancelUrl,
    })
    .then((res) => {
      emit("closeModal");
      window.location.replace(res.data.data.checkout_url);
    })
    .catch((error) => {
      useToast().error(error.response.data.message);
    });
};

// Filter out plans with missing essential data
const validPlans = computed(() => {
  if (!plans || !Array.isArray(plans) || plans.length === 0) return [];

  return plans.filter(plan => {
    return plan &&
           plan.id &&
           plan.name &&
           plan.currency_symbol &&
           (plan.unit_price !== undefined && plan.unit_price !== null);
  });
});

const plan = computed(() => {
  if (validPlans.value.length === 0) return null;

  return validPlans.value.reduce((minPlan, currentPlan) => {
    return currentPlan.unit_price < minPlan.unit_price ? currentPlan : minPlan;
  }, validPlans.value[0]);
});

const priceSuffix = computed(() => {
  switch (plan.value.type) {
    case "recurring":
      return t("billing.per_month");
    case "one_time_payment":
      return t("billing.one_time");
    default:
      return "";
  }
});

const showVAISection = computed(() => {
  return plan.value.type === "one_time_payment";
});
</script>
<template>
  <div class="plan-card">
    <div class="plan-header">
      <h3 class="plan-title">{{ plan.name }}</h3>
      <div class="plan-price">
        <div class="price-main">
          <span class="price-amount">{{ plan.unit_price }}</span>
          <span class="price-currency">{{ plan.currency_symbol }}</span>
        </div>
        <div class="price-period" v-if="priceSuffix">{{ priceSuffix }}</div>
      </div>

      <!-- PROMO40 Banner -->
      <div class="promo-banner">
        <span class="promo-text">{{ t("header.promo_code_banner") }}</span>
      </div>
    </div>

    <div class="plan-features">

      <div class="features-list">
        <div class="feature" v-for="feature in plansBenefits" :key="feature">
          <v-icon icon="mdi-check-circle" color="success" size="18" class="feature-icon" />
          <span class="feature-text">{{ feature }}</span>
        </div>
      </div>
    </div>

    <div class="plan-actions">
      <button class="btn-primary" @click="() => createSubscription(plan.id, 'stripe')">
        {{ t("billing.activate").toUpperCase() }}
      </button>

      <div v-if="showVAISection" class="discount-section">
        <div class="discount-label">
          <span class="star-icon">★</span>
          {{ t("billing.crypto_discount") }}
        </div>
        <button class="btn-discount" @click="() => createSubscription(plan.id, 'vaiot')">
          {{ t("billing.choose") }}
        </button>
      </div>
    </div>

    <!-- Popular Badge -->
    <div v-if="plan.type === 'one_time_payment'" class="popular-badge">
      <span>{{ locale === 'pl' ? 'POLECANE' : 'POPULAR' }}</span>
    </div>
  </div>
</template>
<style scoped lang="scss">
.plan-card {
  width: 260px;
  min-height: 460px;
  background: white;
  border: 1px solid var(--grey);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 24px 20px;
  position: relative;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  @media (max-width: 1200px) {
    width: 300px;
    padding: 24px;
    min-height: 480px;
  }

  @media (max-width: 1100px) {
    width: 280px;
    padding: 22px;
    min-height: 470px;
  }

  @media (max-width: 1000px) {
    width: 270px;
    padding: 20px;
    min-height: 460px;
  }

  @media (max-width: 900px) {
    width: 260px;
    padding: 20px;
    min-height: 450px;
  }

  @media (max-width: 820px) {
    width: 250px;
    padding: 18px;
    min-height: 440px;
  }

  @media (max-width: 950px) {
    width: 100%;
    max-width: 500px;
    min-height: auto;
    margin-bottom: 20px;
    padding: 32px;
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
}

.plan-header {
  text-align: center;
  margin-bottom: 32px;
  margin-top: 12px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.plan-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--eerie-black);
  margin: 0 0 12px 0;
  font-family: 'Archivo', sans-serif;
}

.plan-subtitle {
  font-size: 0.875rem;
  color: var(--dark-grey);
  margin-bottom: 12px;
}

.plan-price {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .price-main {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 4px;
  }

  .price-amount {
    font-size: 2rem;
    font-weight: 800;
    color: var(--eerie-black);
  }

  .price-currency {
    font-size: 1rem;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
  }

  .price-period {
    font-size: 0.875rem;
    color: #666;
    text-align: center;
    font-weight: 400;
  }
}

.promo-banner {
  background: #d3f073;
  border: 1px solid #c9e85a;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 16px;

  .promo-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: #1a1a1a;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.plan-features {
  flex: 1;
  margin-bottom: 32px;
}

.available-plans {
  margin-bottom: 20px;
}

.plans-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--eerie-black);
  text-align: center;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.plans-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.plan-item {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--light-grey);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.813rem;
}

.check-icon {
  color: var(--cambridge-blue);
  font-weight: bold;
  font-size: 0.75rem;
}

.plan-name {
  color: var(--dark-grey);
}

.features-list {
  .feature {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;
    font-size: 0.875rem;
    color: #555;
    line-height: 1.5;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .feature-icon {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .feature-text {
    flex: 1;
  }
}

.plan-actions {
  margin-top: auto;
}

.btn-primary {
  width: 100%;
  background: var(--blue-bonnet);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Archivo', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    background: var(--egyptian-blue);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 35, 239, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.discount-section {
  border: 1px solid var(--booger-buster);
  border-radius: 8px;
  padding: 12px;
  background: #f9fdf4;
}

.discount-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--eerie-black);
  margin-bottom: 10px;
  white-space: normal;
  word-break: break-word;
  text-align: center;
  line-height: 1.3;

  @media (max-width: 1200px) {
    font-size: 0.8rem;
    gap: 4px;
  }

  @media (max-width: 1000px) {
    font-size: 0.75rem;
    gap: 3px;
  }

  @media (max-width: 950px) {
    white-space: normal;
    text-align: center;
    line-height: 1.3;
  }
}

.star-icon {
  color: var(--booger-buster);
  font-size: 0.9rem;
}

.btn-discount {
  width: 100%;
  background: var(--booger-buster);
  color: var(--eerie-black);
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Archivo', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #c9e85a;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(211, 240, 115, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
}

.popular-badge {
  position: absolute;
  top: -1px;
  left: -1px;
  background: #0023ef;
  color: white;
  padding: 8px 16px;
  border-radius: 16px 0 16px 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 3;
  transform: none;
}
</style>
